import {
  Stem,
  StemFractionModel,
  StemFractionModelConstruct,
} from "@examspark/types";

import { getNewScore } from ".";

const scoreBarFractionConstruct = (
  config: NonNullable<StemFractionModelConstruct["config"]["model"]>,
  actual_shaded: number[][][] | undefined,
  expectedNumerator: number,
  expectedDenominator: number,
  expectedSizes: number[],
): boolean => {
  let structureCorrect = true;
  const actualSizes = getSizesFromShaded(actual_shaded);
  if (expectedNumerator > expectedDenominator) {
    const totalCells = actualSizes[0] * actualSizes[1] * actualSizes[2];
    if (
      totalCells < expectedNumerator ||
      actualSizes[1] < expectedDenominator ||
      actualSizes[2] !== expectedSizes[2]
    ) {
      structureCorrect = false;
    }
  } else {
    if (
      actualSizes[0] !== expectedSizes[0] ||
      actualSizes[1] !== expectedSizes[1] ||
      actualSizes[2] !== expectedSizes[2]
    ) {
      structureCorrect = false;
    }
  }
  return structureCorrect;
};

const scorePieFractionConstruct = (
  config: NonNullable<StemFractionModelConstruct["config"]["model"]>,
  actual_shaded: number[][][] | undefined,
  expectedNumerator: number,
  expectedDenominator: number,
  shadedCount: number,
): boolean => {
  let structureCorrect = true;
  const actualSizes = getSizesFromShaded(actual_shaded);
  const totalCells = actualSizes[0] * actualSizes[1] * actualSizes[2];
  if (expectedNumerator > expectedDenominator) {
    if (
      totalCells < expectedNumerator ||
      (actualSizes[1] < expectedDenominator && actualSizes[0] === 1)
    ) {
      structureCorrect = false;
    }
  } else {
    if (totalCells < shadedCount) {
      structureCorrect = false;
    }
  }
  return structureCorrect;
};

export const scoreFractionModel = (
  expected: StemFractionModel | StemFractionModelConstruct,
  actual: Partial<Stem.Response>,
) => {
  const { scoring, config } = expected;

  const score = getNewScore();

  if (!config.model) {
    return score;
  }

  score.max = 1;

  if (!actual) return score;
  let correct = false;
  let structureCorrect = true;

  // handle old vs. new response format
  const actual_shaded =
    actual.shadedWithGroups || (actual.shaded ? [actual.shaded] : undefined);

  // Score responses - check if the shaded count is correct
  const shadedCount = getShadedCount(actual_shaded);
  const shadedCountCorrect = Number(scoring?.shaded?.[0].value) === shadedCount;

  if (expected.interactionType === "fraction-model-construct") {
    const expectedNumerator = Number(scoring?.shaded?.[0].value) || 0;
    const expectedDenominator = Number(config.model.columns) || 0;
    const expectedGroups = Math.max(
      Number(config.model.groups) || 1,
      Math.ceil(expectedNumerator / expectedDenominator),
    );
    const expectedSizes = [
      expectedGroups,
      expectedDenominator,
      Number(config.model.rows),
    ];
    const isOldFormat = !!(actual.shaded && !actual.shadedWithGroups);

    if (config.model.shape === "bar") {
      structureCorrect = isOldFormat
        ? true
        : scoreBarFractionConstruct(
            config.model,
            actual_shaded,
            expectedNumerator,
            expectedDenominator,
            expectedSizes,
          );
    }
    if (config.model.shape === "pie") {
      structureCorrect = isOldFormat
        ? true
        : scorePieFractionConstruct(
            config.model,
            actual_shaded,
            expectedNumerator,
            expectedDenominator,
            shadedCount,
          );
    }
  }

  // Only mark as correct if BOTH the shaded count and structure are correct
  correct = shadedCountCorrect && structureCorrect;

  score.actual = correct ? 1 : 0;
  return score;
};

export const getShadedCount = (
  shaded?: Stem.Response["shaded"] | Stem.Response["shadedWithGroups"],
) => shaded?.flat(3).reduce((a, c) => a + c, 0) ?? 0;

const getSizesFromShaded = (shaded?: number[][][]) => {
  const sizes = [0, 0, 0];

  sizes[0] = shaded?.length ?? 0;
  sizes[1] = shaded?.[0].length ?? 0;
  sizes[2] = shaded?.[0]?.[0]?.length ?? 0;

  return sizes;
};
